import $ from "jquery";
import UI from "~/ui";
import { handleError } from "~/auth";
import { startCountdown } from "~/utils/CountdownTimer";
import { t } from "~/hooks/useI18n";
import { SchedaVenditaState, formattaDataConOrario, startCountdownOn } from "./SchedaVenditaState";

/**
 * Manager della logica Asta Telematica:
 * - caricamento iniziale stato vendita
 * - polling ricorrente
 * - aggiornamento UI (prezzo, cauzione, countdown, box stato)
 * - toggle pulsanti in base al ruolo utente e allo stato offerte
 */
export class AstaTelematicaManager {
    private readonly state: SchedaVenditaState;
    private pollingIdAT: number | null = null;
    private dataFineGaraAT: Date | null = null;
    private dataFinePresentazioneAT: string = "";
    private ATPresentazioneOfferteIncorso: boolean = false;
    private ATGaraInCorso: boolean = false;
    private statoGaraATCorrente: number = 0;

    constructor(state: SchedaVenditaState) {
        this.state = state;
    }

    /**
     * Carica per la prima volta i dati AT e aggiorna la UI/pulsanti.
     */
    public async init(): Promise<void> {
        try {
            await this.refreshDati();
            await this.aggiornaPulsanti();
        } catch (error) {
            console.error("Errore durante l'aggiornamento dell'asta telematica:", error);
            handleError(error);
        }
    }

    /**
     * Avvia un polling ogni 5 secondi per aggiornare stato e pulsanti.
     */
    public startPolling(): void {
        this.pollingIdAT = window.setInterval(() => {
            this.refreshDati().then(() => {
                const promises: Promise<void>[] = [];
                if (this.statoGaraATCorrente === 1 || this.statoGaraATCorrente === 2) {
                    promises.push(this.aggiornaPulsanti());
                }
                return Promise.all(promises);
            }).catch(err => console.error("Errore nel polling:", err));
        }, 5000);
    }

    public stopPolling(): void {
        if (this.pollingIdAT !== null) {
            clearInterval(this.pollingIdAT);
            this.pollingIdAT = null;
        }
    }

    private async refreshDati(): Promise<void> {
        if (this.state.IdAstaTelematica <= 0) {
            return;
        }
        const url = `${window.BaseUrlWebApi}AsteTelematiche/StatoVendita/?idAstaTelematica=${this.state.IdAstaTelematica}`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Errore nella richiesta: ${response.statusText}`);
        }
        const data = await response.json();
        this.visualizzaDati(data);
        this.visualizzaBoxStato(data);
    }

    private visualizzaDati(data: any): void {
        $("#prezzobase").text(UI.formatEuroCurrency(data.PrezzoBase));

        if (data.PrezzoRiserva > 0) {
            $("#offertaminima").text(UI.formatEuroCurrency(data.PrezzoRiserva));
        } else {
            $("#div-offerta-minima").addClass("d-none");
        }

        this.displayCauzioneInfo(data);

        if (data.RialzoMinimo > 0) {
            $("#rialzo-minimo").html(UI.formatEuroCurrency(data.RialzoMinimo));
            $(".rilancio").removeClass("d-none");
        } else {
            $(".rilancio").addClass("d-none");
        }

        const dataInizioCauzione = data.DataInizioCauzione;
        $("#datainiziocauzione").text(dataInizioCauzione ? formattaDataConOrario(dataInizioCauzione) : "-");

        this.dataFinePresentazioneAT = data.DataFineCauzione;
        $("#datafinecauzione").text(this.dataFinePresentazioneAT ? formattaDataConOrario(this.dataFinePresentazioneAT) : "-");

        const oggi = new Date();
        oggi.setSeconds(0, 0);
        const dataFine = new Date(this.dataFinePresentazioneAT);
        dataFine.setSeconds(0, 0);

        this.ATPresentazioneOfferteIncorso = oggi >= new Date(dataInizioCauzione) && oggi <= dataFine;
        if (this.ATPresentazioneOfferteIncorso) {
            $("#divPresentazioneOfferta").addClass("text-body");
            $("#iconaPresentazioneOfferta").addClass("text-success");
        } else {
            $("#divPresentazioneOfferta").removeClass("text-body");
            $("#iconaPresentazioneOfferta").removeClass("text-success");
        }

        const dataInizioGaraAT = data.DataInizio;
        $("#datainiziogara").text(dataInizioGaraAT ? formattaDataConOrario(dataInizioGaraAT) : "-");

        const fineasta = new Date(data.DataFine);
        this.dataFineGaraAT = fineasta;
        $("#datafinegara").text(this.dataFineGaraAT ? formattaDataConOrario(this.dataFineGaraAT) : "-");

        // Stato gara: 1 da iniziare, 2 in corso, 3 conclusa, 4 aggiudicata, 5 chiusa, 6 sospesa
        const today = new Date();
        const inizioasta = new Date(data.DataInizio);
        let statoGara: number = Number(data.Stato);

        const countdownDiv = $("#countdown");
        if (today < inizioasta) {
            statoGara = 1;
            countdownDiv.hide();
        } else if (today > fineasta) {
            $("#div-count-down").addClass("d-none");
            this.stopPolling();
            if (statoGara === 1 || statoGara === 2 || statoGara === 3) {
                statoGara = 3;
            }
        }

        this.statoGaraATCorrente = statoGara;

        this.ATGaraInCorso = (oggi >= new Date(data.DataInizio) && oggi <= new Date(data.DataFine) && data.Stato == 2);
        if (this.statoGaraATCorrente === 2) {
            $("#divGara").addClass("text-body");
            $("#iconaGara").addClass("text-success");
            $("#spInCorso").removeClass("d-none");
            $("#divTempoRimanente").removeClass("d-none");
            countdownDiv.show();
            if (this.dataFineGaraAT) {
                startCountdownOn(this.dataFineGaraAT, countdownDiv[0] as HTMLDivElement, startCountdown);
            }
        } else {
            $("#divGara").removeClass("text-body");
            $("#iconaGara").removeClass("text-success");
            $("#spInCorso").addClass("d-none");
            countdownDiv.hide();
            $("#divTempoRimanente").addClass("d-none");
            if (data.Stato !== 1) {
                this.stopPolling();
            }
        }

        if (data.PrezzoCorrente) {
            $("#divMiglioreOfferta").removeClass("d-none");
            $("#miglioreofferta").text(data.PrezzoCorrente.toLocaleString("it-IT", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }));
        } else {
            $("#divMiglioreOfferta").addClass("d-none");
        }
    }

    private displayCauzioneInfo(data: any): void {
        const cauzioneElement = $("#cauzione");
        switch (data.TipoCauzione) {
            case 1: cauzioneElement.text(UI.formatEuroCurrency(data.Cauzione)); break;
            case 2: cauzioneElement.text(data.Cauzione + t("common.percPrezzoOfferto")); break;
            case 3: cauzioneElement.text(data.Cauzione + t("common.percPrezzoBase")); break;
            case 4: cauzioneElement.text(t("common.nessunaCauzione")); break;
            default: cauzioneElement.text("-"); break;
        }
    }

    private visualizzaBoxStato(data: any): void {
        const today = new Date();
        const statoGara: number = data.Stato;

        if (statoGara === 5) {
            $("#divAstaConclusa").addClass("d-none");
            $("#divAstaChiusa").removeClass("d-none");
            this.stopPolling();
            $("#divTempoRimanente").addClass("d-none");
            $("#countdown").hide();
        } else if (statoGara === 3 || today > new Date(data.DataFine)) {
            $("#divAstaConclusa").removeClass("d-none");
            $("#divAstaChiusa").addClass("d-none");
            this.stopPolling();
            $("#divTempoRimanente").addClass("d-none");
            $("#countdown").hide();
        } else {
            $("#divAstaConclusa").addClass("d-none");
            $("#divAstaChiusa").addClass("d-none");
        }
    }

    private async aggiornaPulsanti(): Promise<void> {
        const ui = this.state.userInfo;
        if (ui?.isAuthenticated && ui?.anagraficaId) {
            const userId = parseInt(ui.anagraficaId);
            if (userId === this.state.venditoreId) {
                this.togglePulsanti({ btnPartecipaAT: false, btnGestisciOffertaAT: false, btnMonitoraAT: true });
                return;
            }
            const hasOffer = await this.checkUserStatus(userId);
            if (hasOffer.ADBidSent) {
                this.togglePulsanti({ btnPartecipaAT: false, btnGestisciOffertaAT: true, btnMonitoraAT: false });
                $("#divOffertaInAttesa").addClass("d-none");
            } else if (hasOffer.ADBidToBeSent && (new Date(this.dataFinePresentazioneAT) > new Date())) {
                this.togglePulsanti({ btnPartecipaAT: false, btnGestisciOffertaAT: false, btnMonitoraAT: false });
                $("#divOffertaInAttesa").removeClass("d-none");
            } else if (hasOffer.ATPin && hasOffer.ATPin.trim().length > 0) {
                this.togglePulsanti({ btnPartecipaAT: false, btnGestisciOffertaAT: true, btnMonitoraAT: false });
                $("#divOffertaInAttesa").addClass("d-none");
            } else if (hasOffer.ATPin === "" && this.ATPresentazioneOfferteIncorso) {
                this.togglePulsanti({ btnPartecipaAT: true, btnGestisciOffertaAT: false, btnMonitoraAT: false });
                $("#divOffertaInAttesa").addClass("d-none");
            } else {
                this.togglePulsanti({ btnPartecipaAT: false, btnGestisciOffertaAT: false, btnMonitoraAT: false });
                $("#divOffertaInAttesa").addClass("d-none");
            }
        } else if (this.ATPresentazioneOfferteIncorso) {
            this.togglePulsanti({ btnPartecipaAT: true, btnGestisciOffertaAT: false, btnMonitoraAT: false });
        } else {
            this.togglePulsanti({ btnPartecipaAT: false, btnGestisciOffertaAT: false, btnMonitoraAT: false });
        }
    }

    private togglePulsanti(visibility: {
        btnPartecipaAT: boolean;
        btnGestisciOffertaAT: boolean;
        btnMonitoraAT: boolean;
    }): void {
        $("#btnPartecipaAT").toggleClass("d-none", !visibility.btnPartecipaAT);
        $("#btnGestisciOffertaAT").toggleClass("d-none", !visibility.btnGestisciOffertaAT);
        $("#btnMonitoraAT").toggleClass("d-none", !visibility.btnMonitoraAT);
    }

    private checkUserStatus(userId: number): Promise<any> {
        return new Promise((resolve, reject) => {
            $.ajaxWithCredentials({
                url: window.BaseUrlWeb + "Acquirente/CheckUserStatus",
                method: "POST",
                data: {
                    UserId: userId,
                    Auctionid: this.state.IdAstaTelematica,
                    VenditaId: this.state.venditaId
                }
            })
                .done((data: any) => resolve(data))
                .fail((jqXHR: any, textStatus: string, errorThrown: string) => {
                    reject({ jqXHR, textStatus, errorThrown });
                });
        });
    }
}
